import {
  Badge,
  Button,
  Flex,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  Table,
  TableContainer,
  TabPanel,
  TabPanels,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import Select from "@/components/Common/AppSelect"
import { InventoryCountApi } from "../../../../api/inventoryCount"
import { WareHouseService } from "../../../../client"
import { PaginationFooter } from "../../../../components/Common/PaginationFooter"
import { WmsListToolbar } from "../../../../components/WMS/shared/WmsListToolbar"
import { WmsPageHeader } from "../../../../components/WMS/shared/WmsPageHeader"
import {
  WmsPagePanel,
  WmsPageShell,
} from "../../../../components/WMS/shared/WmsPageShell"
import { WmsTableListCard } from "../../../../components/WMS/shared/WmsTableListCard"
import { getLicenseErrorDetails } from "../../../../utils/licenseErrors"

export const Route = createFileRoute(
  "/_layout/WMS/reports/inventory-count-alerts",
)({
  component: InventoryCountAlertsPage,
})

function InventoryCountAlertsPage() {
  const now = new Date()
  const defaultTo = now.toISOString().slice(0, 10)
  const defaultFrom = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10)
  const [dateFrom, setDateFrom] = useState(defaultFrom)
  const [dateTo, setDateTo] = useState(defaultTo)
  const [warehouseId, setWarehouseId] = useState("")
  const [drilldownTarget, setDrilldownTarget] = useState<{
    productId: string
    productLabel: string
    locationCode: string
  } | null>(null)
  const [drilldownPage, setDrilldownPage] = useState(1)
  const [drilldownSortBy, setDrilldownSortBy] = useState<
    "created_at" | "status"
  >("created_at")
  const [drilldownSortDir, setDrilldownSortDir] = useState<"asc" | "desc">(
    "desc",
  )
  const drilldownPageSize = 20

  const panelBg = useColorModeValue("white", "gray.800")
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.200")
  const theadBg = useColorModeValue("gray.100", "whiteAlpha.100")
  const zebraBg = useColorModeValue("blackAlpha.50", "whiteAlpha.50")
  const mutedText = useColorModeValue("gray.500", "gray.400")

  const warehouseQuery = useQuery({
    queryKey: ["wms", "warehouses", "list", { includeArchived: false }],
    queryFn: () => WareHouseService.getAllWarehouses(false),
    retry: false,
  })

  const alertsQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "alerts",
      { dateFrom, dateTo, warehouseId },
    ],
    retry: false,
    queryFn: () =>
      InventoryCountApi.getAnomalyAlerts({
        from: dateFrom || undefined,
        to: dateTo || undefined,
        warehouse_id: warehouseId || undefined,
      }),
  })
  const drilldownQuery = useQuery({
    queryKey: [
      "wms",
      "inventory-count",
      "alerts",
      "drilldown",
      {
        dateFrom,
        dateTo,
        warehouseId,
        drilldownTarget,
        drilldownPage,
        drilldownPageSize,
        drilldownSortBy,
        drilldownSortDir,
      },
    ],
    retry: false,
    enabled: Boolean(drilldownTarget),
    queryFn: () =>
      InventoryCountApi.getTaskDrilldown({
        product_id: drilldownTarget?.productId ?? "",
        location_code: drilldownTarget?.locationCode ?? "",
        warehouse_id: warehouseId || undefined,
        from: dateFrom || undefined,
        to: dateTo || undefined,
        page: drilldownPage,
        page_size: drilldownPageSize,
        sort_by: drilldownSortBy,
        sort_dir: drilldownSortDir,
      }),
  })

  const licenseError = alertsQuery.isError
    ? getLicenseErrorDetails(alertsQuery.error)
    : null
  const statusCode =
    typeof alertsQuery.error === "object" &&
    alertsQuery.error !== null &&
    "status" in alertsQuery.error
      ? Number(alertsQuery.error.status)
      : null
  const errorMessage =
    statusCode === 429
      ? "Zbyt wiele zapytań do monitoringu inwentaryzacji. Spróbuj ponownie za chwilę."
      : (licenseError?.description ??
        "Nie udało się pobrać alertów anomalii inwentaryzacji.")

  const spikes = alertsQuery.data?.adjustment_spikes ?? []
  const recounts = alertsQuery.data?.frequent_recounts ?? []

  const topSpikeRatio = useMemo(() => {
    const spikesWithBaseline = spikes.filter(
      (row) => row.baseline_avg_qty > 1e-9,
    )
    if (spikesWithBaseline.length === 0) return 0
    return spikesWithBaseline.reduce(
      (maxValue, row) => Math.max(maxValue, row.spike_ratio),
      0,
    )
  }, [spikes])

  return (
    <WmsPageShell>
      <WmsPageHeader
        title="Alerty inwentaryzacji"
        description="Wykrywanie anomalii: skoki korekt stanów oraz częste ponowne liczenie dla SKU i lokacji."
        gradient="linear(to-r, red.500, orange.500)"
        badge="Raporty / Inwentaryzacja"
      />

      <WmsPagePanel bg={panelBg} borderColor={panelBorder}>
        <WmsListToolbar
          actionsWrap="nowrap"
          filters={
            <>
              <Input
                size="sm"
                type="date"
                maxW={{ base: "100%", lg: "180px" }}
                value={dateFrom}
                onChange={(event) => setDateFrom(event.target.value)}
                borderRadius="xl"
              />
              <Input
                size="sm"
                type="date"
                maxW={{ base: "100%", lg: "180px" }}
                value={dateTo}
                onChange={(event) => setDateTo(event.target.value)}
                borderRadius="xl"
              />
              <Select
                size="sm"
                maxW={{ base: "100%", lg: "240px" }}
                value={warehouseId}
                onChange={(event) => setWarehouseId(event.target.value)}
                borderRadius="xl"
              >
                <option value="">Wszystkie magazyny</option>
                {(warehouseQuery.data ?? []).map((warehouse) => (
                  <option key={warehouse.id} value={warehouse.id}>
                    {warehouse.code} - {warehouse.name}
                  </option>
                ))}
              </Select>
            </>
          }
          actions={
            <Flex align="flex-start" gap={2}>
              <Stat minW={{ base: "120px", md: "160px" }}>
                <StatLabel color={mutedText}>Alerty skoków</StatLabel>
                <StatNumber>{spikes.length}</StatNumber>
              </Stat>
              <Stat minW={{ base: "120px", md: "160px" }}>
                <StatLabel color={mutedText}>Częste ponowne liczenie</StatLabel>
                <StatNumber>{recounts.length}</StatNumber>
              </Stat>
              <Stat minW={{ base: "220px", md: "280px" }}>
                <Flex align="baseline" gap={1} whiteSpace="nowrap">
                  <StatLabel color={mutedText} mb={0}>
                    Najwyższy współczynnik skoku:
                  </StatLabel>
                  <StatNumber fontSize="md" lineHeight="short">
                    {topSpikeRatio > 0 ? `${topSpikeRatio.toFixed(2)}x` : "—"}
                  </StatNumber>
                </Flex>
              </Stat>
            </Flex>
          }
        />

        <Tabs
          variant="enclosed"
          colorScheme="blue"
          display="flex"
          flexDirection="column"
          flex="1"
          minH={0}
          mt={4}
        >
          <TabList px={4}>
            <Tab>
              Skoki korekt stanów
              <Badge ml={2} colorScheme="red">
                {spikes.length}
              </Badge>
            </Tab>
            <Tab>
              Częste recount
              <Badge ml={2} colorScheme="orange">
                {recounts.length}
              </Badge>
            </Tab>
          </TabList>

          <TabPanels flex="1" display="flex" flexDirection="column" minH={0}>
            <TabPanel
              flex="1"
              display="flex"
              flexDirection="column"
              p={4}
              minH={0}
            >
              <WmsTableListCard
                isLoading={alertsQuery.isPending}
                isFetching={alertsQuery.isFetching}
                error={alertsQuery.isError ? alertsQuery.error : undefined}
                errorMessage={errorMessage}
                isEmpty={
                  !alertsQuery.isPending &&
                  !alertsQuery.isError &&
                  spikes.length === 0
                }
                emptyMessage="Brak wykrytych skoków korekt stanów dla wybranego okresu."
                minEmptyHeight={{ base: "180px", lg: "220px" }}
              >
                <TableContainer flex="1" minH={0} overflowY="auto">
                  <Table size="sm" sx={{ tableLayout: "fixed" }}>
                    <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th w="300px">Produkt</Th>
                        <Th w="220px">Magazyn</Th>
                        <Th w="160px">Lokacja</Th>
                        <Th w="160px" isNumeric>
                          Ilość korekt
                        </Th>
                        <Th w="160px" isNumeric>
                          Średnia bazowa
                        </Th>
                        <Th w="140px" isNumeric>
                          Współczynnik skoku
                        </Th>
                        <Th w="140px" isNumeric>
                          Ruchy
                        </Th>
                        <Th w="160px">Szczegóły</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {spikes.map((row, idx) => (
                        <Tr
                          key={`${row.product_id}-${row.warehouse_id}-${row.location_code}-${idx}`}
                          bg={idx % 2 === 0 ? "transparent" : zebraBg}
                        >
                          <Td>
                            <Text fontSize="sm" noOfLines={1}>
                              {[
                                row.product_sku?.trim(),
                                row.product_name?.trim(),
                              ]
                                .filter(Boolean)
                                .join(" - ") || row.product_id}
                            </Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm" noOfLines={1}>
                              {[
                                row.warehouse_code?.trim(),
                                row.warehouse_name?.trim(),
                              ]
                                .filter(Boolean)
                                .join(" - ") || row.warehouse_id}
                            </Text>
                          </Td>
                          <Td>{row.location_code || "—"}</Td>
                          <Td isNumeric>
                            {row.total_adjustment_qty.toFixed(2)}
                          </Td>
                          <Td isNumeric>{row.baseline_avg_qty.toFixed(2)}</Td>
                          <Td isNumeric>
                            {row.baseline_avg_qty <= 1e-9 ? (
                              <Badge colorScheme="orange">brak baseline</Badge>
                            ) : (
                              <Badge colorScheme="red">
                                {row.spike_ratio.toFixed(2)}x
                              </Badge>
                            )}
                          </Td>
                          <Td isNumeric>{row.movements_count}</Td>
                          <Td>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => {
                                setDrilldownPage(1)
                                setDrilldownTarget({
                                  productId: row.product_id,
                                  productLabel:
                                    [
                                      row.product_sku?.trim(),
                                      row.product_name?.trim(),
                                    ]
                                      .filter(Boolean)
                                      .join(" - ") || row.product_id,
                                  locationCode: row.location_code,
                                })
                              }}
                            >
                              Pokaż zadania
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </WmsTableListCard>
            </TabPanel>

            <TabPanel
              flex="1"
              display="flex"
              flexDirection="column"
              p={4}
              minH={0}
            >
              <WmsTableListCard
                isLoading={alertsQuery.isPending}
                isFetching={alertsQuery.isFetching}
                error={alertsQuery.isError ? alertsQuery.error : undefined}
                errorMessage={errorMessage}
                isEmpty={
                  !alertsQuery.isPending &&
                  !alertsQuery.isError &&
                  recounts.length === 0
                }
                emptyMessage="Brak częstego ponownego liczenia dla wybranego okresu."
                minEmptyHeight={{ base: "180px", lg: "220px" }}
              >
                <TableContainer flex="1" minH={0} overflowY="auto">
                  <Table size="sm" sx={{ tableLayout: "fixed" }}>
                    <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                      <Tr>
                        <Th w="300px">Produkt</Th>
                        <Th w="180px">Lokacja</Th>
                        <Th w="160px" isNumeric>
                          Przelicz count
                        </Th>
                        <Th w="160px" isNumeric>
                          Zadanie count
                        </Th>
                        <Th w="160px">Szczegóły</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {recounts.map((row, idx) => (
                        <Tr
                          key={`${row.product_id}-${row.location_code}-${idx}`}
                          bg={idx % 2 === 0 ? "transparent" : zebraBg}
                        >
                          <Td>
                            <Text fontSize="sm" noOfLines={1}>
                              {[
                                row.product_sku?.trim(),
                                row.product_name?.trim(),
                              ]
                                .filter(Boolean)
                                .join(" - ") || row.product_id}
                            </Text>
                          </Td>
                          <Td>{row.location_code || "—"}</Td>
                          <Td isNumeric>
                            <Badge colorScheme="orange">
                              {row.recount_count}
                            </Badge>
                          </Td>
                          <Td isNumeric>{row.task_count}</Td>
                          <Td>
                            <Button
                              size="xs"
                              variant="outline"
                              onClick={() => {
                                setDrilldownPage(1)
                                setDrilldownTarget({
                                  productId: row.product_id,
                                  productLabel:
                                    [
                                      row.product_sku?.trim(),
                                      row.product_name?.trim(),
                                    ]
                                      .filter(Boolean)
                                      .join(" - ") || row.product_id,
                                  locationCode: row.location_code,
                                })
                              }}
                            >
                              Pokaż zadania
                            </Button>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              </WmsTableListCard>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </WmsPagePanel>

      <Modal
        isOpen={Boolean(drilldownTarget)}
        onClose={() => {
          setDrilldownTarget(null)
          setDrilldownPage(1)
        }}
        size="6xl"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Szczegóły tasks
            <Text fontSize="sm" color={mutedText} fontWeight="normal">
              Produkt:{" "}
              {drilldownTarget?.productLabel ??
                drilldownTarget?.productId ??
                "—"}{" "}
              | Lokacja: {drilldownTarget?.locationCode ?? "—"}
            </Text>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Flex mb={3} gap={2} wrap="wrap">
              <Select
                size="sm"
                maxW={{ base: "100%", md: "190px" }}
                value={drilldownSortBy}
                onChange={(event) => {
                  setDrilldownPage(1)
                  setDrilldownSortBy(
                    event.target.value as "created_at" | "status",
                  )
                }}
              >
                <option value="created_at">Sortuj wg daty utworzenia</option>
                <option value="status">Sortuj wg statusu</option>
              </Select>
              <Select
                size="sm"
                maxW={{ base: "100%", md: "170px" }}
                value={drilldownSortDir}
                onChange={(event) => {
                  setDrilldownPage(1)
                  setDrilldownSortDir(event.target.value as "asc" | "desc")
                }}
              >
                <option value="desc">Kierunek: malejąco</option>
                <option value="asc">Kierunek: rosnąco</option>
              </Select>
            </Flex>
            <WmsTableListCard
              isLoading={drilldownQuery.isPending}
              isFetching={drilldownQuery.isFetching}
              error={drilldownQuery.isError ? drilldownQuery.error : undefined}
              errorMessage="Nie udało się pobrać listy zadań szczegółowych."
              isEmpty={
                !drilldownQuery.isPending &&
                !drilldownQuery.isError &&
                (drilldownQuery.data?.items?.length ?? 0) === 0
              }
              emptyMessage="Brak zadań inwentaryzacji dla wybranego SKU i lokacji."
              minEmptyHeight={{ base: "220px", lg: "280px" }}
            >
              <TableContainer maxH="60vh" overflowY="auto" overflowX="auto">
                <Table size="sm" sx={{ tableLayout: "auto" }} minW="980px">
                  <Thead bg={theadBg} position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th w="220px" whiteSpace="nowrap">
                        Nr zadania
                      </Th>
                      <Th w="70px" whiteSpace="nowrap">
                        Status
                      </Th>
                      <Th w="40px" isNumeric>
                        Runda
                      </Th>
                      <Th w="50px" isNumeric>
                        Policzona ilość
                      </Th>
                      <Th w="70px" isNumeric>
                        Odchylenie qty
                      </Th>
                      <Th w="80px" isNumeric>
                        Odchylenie value
                      </Th>
                      <Th w="180px">Utworzono</Th>
                      <Th w="80px">Task</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(drilldownQuery.data?.items ?? []).map((row, idx) => (
                      <Tr
                        key={`${row.task_id}-${idx}`}
                        bg={idx % 2 === 0 ? "transparent" : zebraBg}
                      >
                        <Td>
                          <Text noOfLines={1} title={row.task_no}>
                            {row.task_no}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme="purple" whiteSpace="nowrap">
                            {row.status}
                          </Badge>
                        </Td>
                        <Td isNumeric>{row.count_round}</Td>
                        <Td isNumeric>
                          {typeof row.counted_qty === "number"
                            ? row.counted_qty.toFixed(2)
                            : "—"}
                        </Td>
                        <Td isNumeric>
                          {typeof row.variance_qty === "number"
                            ? row.variance_qty.toFixed(2)
                            : "—"}
                        </Td>
                        <Td isNumeric>
                          {typeof row.variance_value === "number"
                            ? row.variance_value.toFixed(2)
                            : "—"}
                        </Td>
                        <Td>
                          {new Date(row.task_created_at).toLocaleString()}
                        </Td>
                        <Td>
                          <Button
                            as="a"
                            href={`/WMS/inventory-count/tasks/${row.task_id}`}
                            size="xs"
                            variant="outline"
                          >
                            Otwórz task
                          </Button>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
              <PaginationFooter
                page={drilldownPage}
                hasPreviousPage={drilldownPage > 1}
                hasNextPage={
                  drilldownPage * drilldownPageSize <
                  (drilldownQuery.data?.total ?? 0)
                }
                onChangePage={setDrilldownPage}
              />
            </WmsTableListCard>
          </ModalBody>
        </ModalContent>
      </Modal>
    </WmsPageShell>
  )
}
